import KrcSnapshotModal, { type KrcSnapshotFormData } from '@UI/modals/KrcSnapshotModal';
import ModalErrorBoundary from '@UI/modals/ModalErrorBoundary';
import type { ContentHref } from '@generalTypes/apiTypes';
import type { RootState } from '@generalTypes/rootStateTypes';
// @ts-ignore shared component package has no published TS declarations
import { Modal } from '@kathondvla/react-shared-components/src/components';
import { selectContentItem } from '@newStore/documentApi/documentApiSelectors';
import {
  persistKrcSnapshot,
  prepareSnapshotChangelog,
} from '@newStore/documentSnapshot/documentSnapshotState';
import {
  selectCurrentDocumentHref,
  selectIsDocumentReadOnly,
  selectIsDocumentSaving,
} from '@newStore/documentUI/documentUISelectors';
import { selectCanUserPersistSnapshot } from '@newStore/user/userSelectors';
import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const KrcSnapshotButton: React.FC = () => {
  const dispatch = useDispatch();
  const snapshotModalRef = useRef<Modal>();

  const canPersistSnapshot = useSelector(selectCanUserPersistSnapshot);
  const isReadOnlyDocument = useSelector(selectIsDocumentReadOnly);
  const isValid = useSelector((state: RootState) => state.document.viewModel.isValid);
  const isSaving = useSelector(selectIsDocumentSaving);

  const documentHref = useSelector(selectCurrentDocumentHref);
  const currentDocument = useSelector((state: RootState) =>
    documentHref ? selectContentItem(state, documentHref as ContentHref) : null,
  );
  const snapshotChangelogStatus = useSelector(
    (state: RootState) => state.documentSnapshot.snapshotChangelogStatus,
  );

  const isPreparingChangelog = snapshotChangelogStatus === 'loading';
  const isDisabled = !isValid || isReadOnlyDocument || isSaving || isPreparingChangelog;

  const handleOpen = () => {
    if (isDisabled) {
      return;
    }

    dispatch(prepareSnapshotChangelog());
    snapshotModalRef.current?.open({
      initialValues: {
        startDate: currentDocument?.current?.startDate ?? '',
      },
      onConfirm: (data: KrcSnapshotFormData) => {
        dispatch(persistKrcSnapshot({ mode: 'new', ...data }));
      },
    });
  };

  if (!canPersistSnapshot) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className="btn btn-default buttonRelative"
        onClick={handleOpen}
        disabled={isDisabled}
      >
        <div className={isSaving ? 'buttonInvisible' : ''}>
          <span
            className="glyphicon glyphicon-tag"
            style={{ marginRight: '5px', verticalAlign: 'top' }}
          />
          <span>Nieuwe versie publiceren</span>
        </div>
        <div className={!isSaving ? 'buttonInvisible' : 'spinner-icon center'} />
      </button>

      <Modal
        ref={snapshotModalRef}
        size="md"
        component={<KrcSnapshotModal mode="new" />}
        errorBoundaryComponent={ModalErrorBoundary}
      />
    </>
  );
};

KrcSnapshotButton.displayName = 'KrcSnapshotButton';

export default KrcSnapshotButton;
